import React, { useState, useEffect } from 'react';
import { 
  PhotoIcon,
  XMarkIcon,
  PlayIcon
} from '@heroicons/react/24/outline';
import { getImageUrlWithEnv } from '../utils/imageUrl';

// API configuration
const getApiUrl = (endpoint) => {
  const baseUrl = 'https://fciveria.ge';
  return `${baseUrl}${endpoint}`;
};

const Fans = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  useEffect(() => {
    loadPhotos();
  }, []);

  const loadPhotos = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(getApiUrl('/api/photos'));
      if (response.ok) {
        const photosData = await response.json();
        // Filter photos to only show "გულშემატკივრები" category
        const fanPhotos = photosData
          .filter(photo => photo.category === 'გულშემატკივრები' && photo.isPublished)
          .map(photo => ({
            id: photo.id,
            src: photo.imageUrl,
            alt: photo.title || 'გულშემატკივარი ფოტო',
            category: photo.category,
            date: photo.uploadDate ? photo.uploadDate.split('T')[0] : '',
            description: photo.description || ''
          }));
        setPhotos(fanPhotos);
      } else {
        throw new Error('Failed to load photos');
      }
    } catch (err) {
      setError(err.message);
      console.error('Error loading photos:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 font-georgian mb-4">
            გულშემატკივრები
          </h1>
          <p className="text-xl text-gray-600 font-georgian max-w-3xl mx-auto">
            ჩვენი გულშემატკივრების ფოტოები და მომენტები
          </p>
        </div>

        {/* Photo Gallery */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6 flex items-center">
            <PhotoIcon className="h-6 w-6 mr-3" />
            ფოტო გალერია
          </h2>
          
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              <p className="ml-4 text-gray-600 font-georgian">ფოტოების ჩატვირთვა...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <p className="text-red-600 font-georgian mb-4">შეცდომა: {error}</p>
              <button 
                onClick={loadPhotos}
                className="bg-primary-600 hover:bg-primary-700 text-white font-bold py-2 px-4 rounded font-georgian"
              >
                ხელახლა ცდა
              </button>
            </div>
          ) : photos.length === 0 ? (
            <div className="text-center py-12">
              <PhotoIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-georgian">გულშემატკივრების ფოტოები ჯერ არ არის ხელმისაწვდომი</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {photos.map((image) => (
                <div
                  key={image.id}
                  className="relative group cursor-pointer"
                  onClick={() => setSelectedImage(image)}
                >
                  <div className="aspect-w-4 aspect-h-3 bg-gray-200 rounded-lg overflow-hidden">
                    <img
                      src={getImageUrlWithEnv(image.src)}
                      alt={image.alt}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.target.src = '/api/placeholder/400/300';
                      }}
                    />
                  </div>
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all duration-300 rounded-lg flex items-center justify-center">
                    <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <PhotoIcon className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 rounded-b-lg">
                    <p className="text-white font-georgian text-sm font-medium">{image.category}</p>
                    <p className="text-gray-300 font-georgian text-xs">{image.date}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* YouTube Live Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 font-georgian mb-6 flex items-center">
            <PlayIcon className="h-6 w-6 mr-3 text-red-600" />
            YouTube Live
          </h2>
          <div className="text-center mb-6">
            <p className="text-gray-600 font-georgian mb-4">
              მიჰყევით ჩვენს YouTube არხს ცოცხალი ტრანსლაციებისთვის
            </p>
            <a
              href="https://www.youtube.com/@IveriaArena/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-6 rounded-lg transition-colors duration-200 font-georgian"
            >
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
              YouTube არხზე გადასვლა
            </a>
          </div>
          
          {/* YouTube Channel Embed */}
          <div className="bg-gray-100 rounded-lg p-6">
            <div className="aspect-w-16 aspect-h-9 bg-gray-200 rounded-lg overflow-hidden">
              <iframe
                src="https://www.youtube.com/embed/videoseries?list=UU@IveriaArena"
                title="Iveria Arena YouTube Channel"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
            <div className="mt-4 text-center">
              <h3 className="text-lg font-semibold text-gray-900 font-georgian mb-2">
                Iveria Arena - ოფიციალური YouTube არხი
              </h3>
              <p className="text-sm text-gray-600 font-georgian">
                ცოცხალი ტრანსლაციები, მატჩების ჰაილაითები და ექსკლუზიური კონტენტი
              </p>
            </div>
          </div>
        </div>

        {/* Image Modal */}
        {selectedImage && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="relative max-w-4xl max-h-full">
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 text-white hover:text-gray-300 z-10"
              >
                <XMarkIcon className="h-8 w-8" />
              </button>
              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="max-w-full max-h-full rounded-lg"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-6 rounded-b-lg">
                <h3 className="text-white font-georgian text-lg font-semibold mb-2">
                  {selectedImage.category}
                </h3>
                <p className="text-gray-300 font-georgian text-sm">{selectedImage.date}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Fans;
